import { computed, h, ref, Ref } from "vue";
import Tag from "primevue/tag";
import type { BpmnElement } from "bpmn-js/lib/model/Types";
import type Overlays from "diagram-js/lib/features/overlays/Overlays";
import type Canvas from "diagram-js/lib/core/Canvas";
import type { CanvasViewbox } from "diagram-js/lib/core/Canvas";
import { is } from "bpmn-js/lib/util/ModelUtil";
import type { useBpmnManager } from "./index";
// @ts-expect-error: Package doesn't have types.
import { diff as bpmnJsDiff } from "bpmn-js-differ";
import { NavigatedViewer } from "./helpers";
import { vnodeToHtml } from "@/utils";

export type ChangeType = "added" | "removed" | "changed" | "layoutChanged";

export interface Change {
    element: BpmnElement;
    type: ChangeType;
}

interface Marker {
    overlayId: string;
    elementId: string;
    type: ChangeType;
    className: `diff-${ChangeType}`;
}

const typeToSeverity: Record<ChangeType, string> = {
    "added": "success",
    "layoutChanged": "secondary",
    "changed": "warn",
    "removed": "danger",
};

const typeToIcon: Record<ChangeType, string> = {
    "added": "pi pi-plus",
    "layoutChanged": "pi pi-palette",
    "changed": "pi pi-pencil",
    "removed": "pi pi-minus",
};

export function useBpmnDiffManager(
    bpmnFromManager: ReturnType<typeof useBpmnManager<NavigatedViewer>>,
    bpmnToManager: ReturnType<typeof useBpmnManager<NavigatedViewer>>
) {
    const layoutChanged: Ref<Record<string, BpmnElement>> = ref({});
    const changed: Ref<Record<string, BpmnElement>> = ref({});
    const added: Ref<Record<string, BpmnElement>> = ref({});
    const removed: Ref<Record<string, BpmnElement>> = ref({});

    // Markers are drawn elements
    let markers: Marker[] = [];

    /**
     * Set the XML for either modeler instance.
     */
    const setXml = async (which: "from" | "to", xml: string) => {
        if (which === "from") {
            return await bpmnFromManager.loadBpmn(xml);
        } else if (which === "to") {
            return await bpmnToManager.loadBpmn(xml);
        }
        throw new Error(
            "Invalid `which` selected. Should be one of `from` or `to`."
        );
    };

    /**
     * Get the changes between both BPMN Viewers.
     *
     * Raises when one of the XML's is invalid
     */
    const run = () => {
        layoutChanged.value = {};
        changed.value = {};
        added.value = {};
        removed.value = {};

        if (!bpmnFromManager.xml.value || !bpmnToManager.xml.value) {
            // The Definitions aren't properly cleared when the value is unset.
            return;
        }

        const changeHandler = bpmnJsDiff(
            bpmnFromManager.bpmnJs.value?.getDefinitions(),
            bpmnToManager.bpmnJs.value?.getDefinitions()
        );

        /**
         * Filter out any bpmn:Collaboration objects.
         */
        const filterCollaborations = (changes: Record<string, BpmnElement>) => {
            return Object.fromEntries(
                Object.entries(changes).filter(
                    ([_, value]) => !is(value, "bpmn:Collaboration")
                )
            ) as Record<string, BpmnElement>;
        };

        layoutChanged.value = filterCollaborations(
            changeHandler._layoutChanged
        );
        changed.value = filterCollaborations(changeHandler._changed);
        added.value = filterCollaborations(changeHandler._added);
        removed.value = filterCollaborations(changeHandler._removed);
    };

    /**
     * Mark the changes inside the viewers.
     */
    const markChanges = () => {
        clearMarkers();

        const fromBpmnJs = bpmnFromManager.bpmnJs.value;
        const toBpmnJs = bpmnToManager.bpmnJs.value;
        if (!fromBpmnJs || !toBpmnJs) {
            throw Error("BpmnJs instances are not available.");
        }

        for (const id in changed.value) {
            addMarker(fromBpmnJs, id, "changed");
            addMarker(toBpmnJs, id, "changed");
        }

        for (const id in removed.value) {
            addMarker(fromBpmnJs, id, "removed");
        }

        for (const id in added.value) {
            addMarker(toBpmnJs, id, "added");
        }
    };

    /**
     * Clears all markers created by addMarker.
     */
    const clearMarkers = () => {
        while (markers.length) {
            // Remove from array
            const marker = markers.shift();
            if (!marker) {
                continue;
            }
            removeMarker(marker, bpmnFromManager.bpmnJs.value);
            removeMarker(marker, bpmnToManager.bpmnJs.value);
        }
    };

    /**
     * Remove a given marker from a given viewer.
     */
    const removeMarker = (marker: Marker, viewer?: NavigatedViewer) => {
        if (!viewer) return;
        const overlays = viewer.get<Overlays>("overlays");
        const canvas = viewer.get<Canvas>("canvas");

        try {
            overlays.remove(marker.overlayId);
        } catch {
            // Element might already be removed or was never there.
        }
        try {
            canvas.removeMarker(marker.elementId, marker.className);
        } catch {
            // Element might already be removed or was never there.
        }
    };

    /**
     * Add marker for Bpmn Diff
     */
    const addMarker = (
        viewer: NavigatedViewer,
        elementId: string,
        type: ChangeType
    ) => {
        if (!viewer) return;
        const overlays = viewer.get<Overlays>("overlays");
        const marker: Marker = {
            overlayId: "",
            elementId: elementId,
            type: type,
            className: `diff-${type}`,
        };

        const html = vnodeToHtml(
            h(Tag, {
                icon: typeToIcon[type],
                severity: typeToSeverity[type],
            })
        );

        const position = { top: -5, left: -5 };

        try {
            marker.overlayId = overlays.add(
                marker.elementId,
                marker.className,
                {
                    position,
                    html,
                }
            );

            // Add class to element itself as well.
            viewer
                .get<Canvas>("canvas")
                .addMarker(marker.elementId, marker.className);

            markers.push(marker);
        } catch (e) {
            console.warn(`Could not add marker for: '${elementId}'.`, {
                viewer,
                error: e,
            });
        }
    };

    /**
     * Sync the navigation in both viewers.
     *
     * Returns a function which unsyncs the viewers when called.
     */
    const syncViewers = () => {
        let changing: boolean;
        if (!bpmnFromManager.bpmnJs.value || !bpmnToManager.bpmnJs.value) {
            throw Error("BpmnJs instances are not available.");
        }

        function update(viewer: NavigatedViewer) {
            return function (event: { viewbox: CanvasViewbox }) {
                if (changing) {
                    return;
                }

                changing = true;
                viewer.get<Canvas>("canvas").viewbox(event.viewbox);
                changing = false;
            };
        }

        function syncViewbox(a: NavigatedViewer, b: NavigatedViewer) {
            const handler = update(b);
            a.on("canvas.viewbox.changed", handler);
            return () => a.off("canvas.viewbox.changed", handler);
        }

        const unsyncA = syncViewbox(
            bpmnFromManager.bpmnJs.value,
            bpmnToManager.bpmnJs.value
        );
        const unsyncB = syncViewbox(
            bpmnToManager.bpmnJs.value,
            bpmnFromManager.bpmnJs.value
        );

        return () => {
            unsyncA();
            unsyncB();
        };
    };

    return {
        run,
        setXml,
        syncViewers,
        markChanges,
        typeToSeverity,
        changes: computed<Change[]>(() => {
            return ([] as Change[]).concat(
                Object.values(layoutChanged.value).map((element) => {
                    return { element, type: "layoutChanged" };
                }),
                Object.values(changed.value).map((element) => {
                    return { element, type: "changed" };
                }),
                Object.values(added.value).map((element) => {
                    return { element, type: "added" };
                }),
                Object.values(removed.value).map((element) => {
                    return { element, type: "removed" };
                })
            );
        }),
    };
}
